/**
 * MBMR readiness API (P0) — `GET /bp/mbmr/readiness/`.
 *
 * Contract: scratchpad/readiness_contract.md (shared with the backend lane RDY-BE).
 *   ?recipe_id=<id>     whole recipe (tree + roll-up) — fetched once when the editor opens
 *   ?process_id=<id>    one process
 *   ?operation_id=<id>  one operation — used to refresh the rail after a save
 *
 * Read-only. No polling: the recipe call happens on open, the operation call after a save.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  THE FLAG
 *  RDY-BE is building the endpoint in parallel. While `ALLOW_READINESS_MOCK` is true
 *  we still call the real endpoint FIRST and use it whenever it answers; the mock is
 *  only the fallback for a 404/501. Flip the flag to false once the endpoint has
 *  shipped and the mock disappears entirely (an outage then surfaces as an error in
 *  the rail instead of silently showing invented data).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import Axios from 'axios';

const bp = '/bp';
export const READINESS_URL = `${bp}/mbmr/readiness/`;

/**
 * ← single flag. Set to false once /bp/mbmr/readiness/ is live.
 * FLIPPED: RDY-BE shipped the endpoint and it was verified end-to-end against recipe 1
 * (all three query forms). The mock below is dead code kept only so the flag can be
 * flipped back if the endpoint has to be rolled out again.
 */
export const ALLOW_READINESS_MOCK = false;

const authHeaders = () => {
  // The app authenticates with an HttpOnly session cookie (see utils/api.js); the
  // bearer header is kept for parity with the other /bp/* callers and is ignored
  // by the server when a cookie is present.
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * @param {{recipe_id?:number|string, process_id?:number|string, operation_id?:number|string}} params
 * @returns {Promise<{data:object, source:'live'|'mock'}>}
 */
export const getReadiness = async (params, signal = null) => {
  try {
    const res = await Axios.get(READINESS_URL, { params, signal, headers: authHeaders() });
    const body = res?.data;
    if (body?.status === 'success' && body?.data) return { data: body.data, source: 'live' };
    throw new Error(body?.message || 'Unexpected readiness payload');
  } catch (err) {
    if (Axios.isCancel?.(err) || err?.code === 'ERR_CANCELED') throw err;
    if (!ALLOW_READINESS_MOCK) throw err;
    const data = await buildMockReadiness(params, signal);
    return { data, source: 'mock' };
  }
};

// ───────────────────────── lifecycle actions (RDY-LIFE) ─────────────────────────
// The write half of the contract. Both endpoints answer with the SAME `lifecycle` block the
// readiness payload carries, so a caller can splice the fresh state straight into the model
// instead of refetching the whole tree.

export const RETURN_URL = `${bp}/mbmr/return/`;
export const RESUBMIT_URL = `${bp}/mbmr/resubmit/`;

/** Read the server's own words back to the user — a refusal here is a compliance rule
 *  (separation of duties, a role, a reason that is too short), never a generic failure. */
const failure = (err) => {
  const body = err?.response?.data;
  const e = new Error(body?.message || err?.message || 'The request failed');
  e.code = body?.code || null;
  e.status = err?.response?.status || null;
  e.body = body || null;
  return e;
};

/**
 * Return an MBMR (or one process) for correction.
 * @param {{recipe_id?:number, process_id?:number, reason:string,
 *          esign:{username:string, password:string, comment?:string},
 *          finding_operation_ids?:number[]}} payload
 */
export const returnForCorrection = async (payload) => {
  try {
    const res = await Axios.post(RETURN_URL, payload, { headers: authHeaders() });
    if (res?.data?.status === 'success') return res.data;
    throw new Error(res?.data?.message || 'The return was refused');
  } catch (err) {
    throw failure(err);
  }
};

/** Put a corrected MBMR back under review. */
export const resubmitForReview = async (payload) => {
  try {
    const res = await Axios.post(RESUBMIT_URL, payload, { headers: authHeaders() });
    if (res?.data?.status === 'success') return res.data;
    throw new Error(res?.data?.message || 'The re-submission was refused');
  } catch (err) {
    throw failure(err);
  }
};

// ───────────────────────────── mock ─────────────────────────────
// The mock is NOT a fixture: it reads the recipe's real process/operation tree from
// `/bp/get_process_operation_by_recipe` and derives readiness from the operation type,
// following the profile table in the contract. That keeps every operation_id real, so
// clicking a blocker in the rail navigates to the operation it actually names.

/** Stable per-operation pseudo-randomness — the same recipe always looks the same. */
const hash = (n) => {
  let h = (Number(n) || 0) * 2654435761;
  h = (h ^ (h >>> 13)) >>> 0;
  return h % 100;
};

/** Contract profile table. `blocking` entries block submission when missing. */
const PROFILES = {
  dispensing: {
    blocking: [
      ['ingredients', 'Ingredients with weighing tolerance'],
      ['weighing_device', 'Weighing device assigned'],
      ['second_person', 'Second-person check set'],
    ],
    optional_checks: [
      ['line_clearance_ref', 'Line-clearance reference'],
      ['instruction', 'Instruction text'],
    ],
  },
  filling_lots: {
    blocking: [
      ['machine', 'Machine assigned'],
      ['lot_size', 'Lot size set'],
      ['parameter_limits', 'IPC parameter with limits'],
      ['sampling', 'Sampling plan'],
    ],
    optional_checks: [
      ['aql', 'AQL scheme'],
      ['campaign_cap', 'Campaign cap'],
      ['instruction', 'Instruction text'],
    ],
  },
  unit_count: {
    blocking: [
      ['work_unit', 'Work unit defined'],
      ['defect_set', 'Defect set defined'],
      ['aql', 'AQL scheme'],
    ],
    optional_checks: [
      ['stage_sequence', 'Stage sequence'],
      ['station_allocation', 'Station allocation'],
      ['instruction', 'Instruction text'],
    ],
  },
  hold: {
    blocking: [
      ['hold_type', 'Hold type set'],
      ['hold_hours', 'Minimum / maximum hours set'],
    ],
    optional_checks: [
      ['storage_condition', 'Storage condition'],
      ['instruction', 'Instruction text'],
    ],
    never: [['sampling', 'Sampling plan optional for this type']],
  },
  documentary: {
    blocking: [
      ['instruction', 'Instruction text present'],
      ['signer', 'Signer assigned'],
    ],
    optional_checks: [['attachment_policy', 'Attachment policy']],
    never: [['parameter_limits', 'Parameters optional for a documentary record']],
  },
  line_clearance: {
    blocking: [
      ['clearance_type', 'Clearance type set'],
      ['checklist', 'Checklist assigned'],
      ['second_person', 'Second person assigned'],
    ],
    optional_checks: [
      ['photo_evidence', 'Photo evidence'],
      ['instruction', 'Instruction text'],
    ],
  },
  standard: {
    blocking: [['parameter_limits', 'Parameter with min and max limits']],
    optional_checks: [
      ['criticality', 'Criticality classified'],
      ['instruction', 'Instruction text'],
      ['sampling', 'Sampling plan'],
    ],
  },
  generic: {
    blocking: [
      ['instruction', 'Instruction text present'],
      ['content', 'Ingredients or parameters configured'],
    ],
    optional_checks: [['signer', 'Signer assigned']],
  },
};

const KNOWN_TYPES = [
  [/line\s*clearance/i, 'line_clearance'],
  [/dispens/i, 'dispensing'],
  [/hold\s*time|^hold$/i, 'hold'],
  [/filling.*lot|lot-?wise/i, 'filling_lots'],
  [/visual\s*inspection|unit\s*count|aql/i, 'unit_count'],
  [/details of|storage|record|reconcil/i, 'documentary'],
  [/collection|charging|equipment setup|cleaning|filtration|integrity|mixing|sterilis|steriliz|filling|debagging|tyvek|measurement|sampling/i, 'standard'],
];

const profileFor = (operationType) => {
  const t = String(operationType || '');
  const hit = KNOWN_TYPES.find(([re]) => re.test(t));
  // Contract: an operation type with no profile falls back to `generic`, and says so.
  return hit ? hit[1] : 'generic';
};

const deriveOperation = (op) => {
  const profile = profileFor(op?.operation_type);
  const spec = PROFILES[profile] || PROFILES.generic;
  const seed = hash(op?.operation_id);

  const checks = [];
  (spec.blocking || []).forEach(([key, label], i) => {
    // Deterministic: only a small slice of operations misses a blocking check.
    const missing = (seed + i * 17) % 100 < 6;
    checks.push({
      key,
      label: missing ? `${label} — missing, blocks submission` : label,
      status: missing ? 'missing' : 'ok',
      blocking: true,
    });
  });
  (spec.optional_checks || []).forEach(([key, label], i) => {
    const missing = (seed + i * 29) % 100 < 22;
    checks.push({
      key,
      label:
        missing && key === 'instruction'
          ? 'No instruction text — the printed record would show an empty step'
          : missing
          ? `${label} not set`
          : label,
      status: missing ? 'missing' : 'ok',
      blocking: false,
    });
  });
  (spec.never || []).forEach(([key, label]) => {
    checks.push({ key, label, status: 'optional', blocking: false });
  });

  const nonOptional = checks.filter((c) => c.status !== 'optional');
  const missing = nonOptional.filter((c) => c.status === 'missing');
  let state;
  if (missing.some((c) => c.blocking)) state = 'blocked';
  else if (nonOptional.length && missing.length === nonOptional.length) state = 'not_started';
  else if (missing.length) state = 'partial';
  else state = 'ready';

  return {
    operation_id: op?.operation_id,
    operation_number: op?.operation_number,
    operation_name: op?.operation_name,
    operation_type: op?.operation_type,
    operation_type_id: op?.operation_type_id,
    profile,
    state,
    checks,
    blocking_reasons: checks.filter((c) => c.status === 'missing' && c.blocking).map((c) => c.label),
  };
};

const worstOf = (states) => {
  if (states.includes('blocked')) return 'blocked';
  if (states.includes('partial')) return 'partial';
  if (states.includes('ready') && states.includes('not_started')) return 'partial';
  if (states.includes('not_started')) return 'not_started';
  return 'ready';
};

const buildMockReadiness = async (params, signal) => {
  const recipeId = params?.recipe_id;
  let raw = [];
  let recipeName = '';
  let isPackaging = false;

  if (recipeId) {
    const res = await Axios.get(`${bp}/get_process_operation_by_recipe`, {
      params: { recipe_id: recipeId },
      signal,
      headers: authHeaders(),
    });
    raw = Array.isArray(res?.data?.data) ? res.data.data : [];
    isPackaging = Boolean(res?.data?.is_packaging);
    recipeName = res?.data?.recipe_name || '';
  }

  const processes = raw.map((p, i) => {
    const operations = (p?.operation || []).map(deriveOperation);
    return {
      process_id: p?.process_id,
      process_name: p?.process_name,
      process_number: i + 1,
      state: worstOf(operations.map((o) => o.state)),
      operations,
    };
  });

  const all = processes.flatMap((p) => p.operations);
  const c = (s) => all.filter((o) => o.state === s).length;
  const blocking = c('blocked');

  const full = {
    recipe_id: recipeId,
    recipe_name: recipeName,
    is_packaging: isPackaging,
    summary: {
      operations_total: all.length,
      ready: c('ready'),
      partial: c('partial'),
      blocked: blocking,
      not_started: c('not_started'),
      submittable: blocking === 0,
      blocking_count: blocking,
    },
    processes,
  };

  // The narrowed forms return the BARE node, matching the live endpoint (the contract
  // only documented the recipe envelope — see normalizeReadiness for the reconciliation).
  if (params?.operation_id) {
    const opId = String(params.operation_id);
    return all.find((o) => String(o.operation_id) === opId) || null;
  }
  if (params?.process_id) {
    const pid = String(params.process_id);
    return processes.find((p) => String(p.process_id) === pid) || null;
  }
  return full;
};
